import { AI_SERVICES_ORIGIN, MOSIDA_API_ORIGIN } from "../config/apiUrls";
import { fetchWithTimeout } from "../utils/httpClient";
import {
  fifaCategoryIconUrl,
  fifaCategorySliderImageUrls,
  readSelectedFifaCategory,
} from "./fifaCategoryService";
import { readSelectedFifaTeam } from "./fifaTeamService";
import {
  DEFAULT_FIFA_PLAYER_STATS,
  readFifaPlayerProfile,
  readFifaPlayerStats,
  readFifaSelfie,
  type FifaPlayerProfile,
  type FifaPlayerStats,
} from "../utils/stationFlowStorage";

const FIFA_GENERATION_ENDPOINT = `${AI_SERVICES_ORIGIN.replace(/\/+$/, "")}/api/generate-fifa-worldcup`;

const FIFA_POSITION_ABBR_TO_VALUE: Record<string, string> = {
  GK: "Goalkeeper",
  RB: "Right Back",
  LB: "Left Back",
  CB: "Center Back",
  RWB: "Right Wing Back",
  LWB: "Left Wing Back",
  CDM: "Central Defensive Midfielder",
  CM: "Central Midfielder",
  CAM: "Central Attacking Midfielder",
  RM: "Right Midfielder",
  LM: "Left Midfielder",
  RW: "Right Winger",
  LW: "Left Winger",
  CF: "Center Forward",
  ST: "Striker",
};

function resolveFifaPositionValue(position: string): string {
  const trimmed = position.trim();
  if (!trimmed) return DEFAULT_FIFA_PLAYER_STATS.position;
  if (FIFA_POSITION_ABBR_TO_VALUE[trimmed]) return trimmed;
  const abbr = Object.entries(FIFA_POSITION_ABBR_TO_VALUE).find(
    ([, value]) => value === trimmed
  )?.[0];
  return abbr || trimmed;
}

export type GenerateFifaWorldCupCardOptions = {
  sourceImageUrl?: string | null;
  profile?: FifaPlayerProfile | null;
  stats?: FifaPlayerStats | null;
  isAiStats?: boolean;
  fifaPrompt?: string | null;
  templateImageUrl?: string | null;
  jerseyImageUrl?: string | null;
  temperature?: number;
};

export type GenerateFifaWorldCupCardResult = {
  success: boolean;
  imageUrl?: string;
  error?: string;
  data?: unknown;
};

type FifaWorldCupCardFormFields = {
  source_url: string;
  template_url: string;
  jersey_url: string;
  club_team: string;
  first_name: string;
  last_name: string;
  jersey_number: string;
  raw_prompt_mode: boolean;
  is_ai_stats: boolean;
  temperature: number;
  fifa_prompt: string;
  position?: string;
  rating?: string;
  pace?: number;
  shooting?: number;
  passing?: number;
  dribbling?: number;
  defending?: number;
  physical?: number;
};

function buildFifaWorldCupCardFormData(fields: FifaWorldCupCardFormFields): FormData {
  const form = new FormData();

  form.append("source_url", fields.source_url);
  form.append("template_url", fields.template_url);
  form.append("jersey_url", fields.jersey_url);
  form.append("club_team", fields.club_team);
  form.append("first_name", fields.first_name);
  form.append("last_name", fields.last_name);
  form.append("jersey_number", fields.jersey_number);
  form.append("raw_prompt_mode", fields.raw_prompt_mode ? "true" : "false");
  form.append("is_ai_stats", fields.is_ai_stats ? "true" : "false");
  form.append("temperature", String(fields.temperature));
  form.append("fifa_prompt", fields.fifa_prompt);

  if (!fields.is_ai_stats) {
    if (fields.position) form.append("position", fields.position);
    if (fields.rating) form.append("rating", fields.rating);
    if (fields.pace !== undefined) form.append("pace", String(fields.pace));
    if (fields.shooting !== undefined) form.append("shooting", String(fields.shooting));
    if (fields.passing !== undefined) form.append("passing", String(fields.passing));
    if (fields.dribbling !== undefined) form.append("dribbling", String(fields.dribbling));
    if (fields.defending !== undefined) form.append("defending", String(fields.defending));
    if (fields.physical !== undefined) form.append("physical", String(fields.physical));
  }

  return form;
}

function resolveFetchableImageUrl(url: string): string {
  const value = url?.trim();

  if (!value) return "";

  if (
    value.startsWith("data:") ||
    value.startsWith("blob:") ||
    /^https?:\/\//i.test(value)
  ) {
    return value;
  }

  if (value.startsWith("//")) {
    return `https:${value}`;
  }

  const origin = MOSIDA_API_ORIGIN.replace(/\/+$/, "");

  return value.startsWith("/")
    ? `${origin}${value}`
    : `${origin}/${value}`;
}

function pickGeneratedImageUrl(json: unknown): string | undefined {
  if (!json || typeof json !== "object") return undefined;

  const record = json as Record<string, unknown>;
  const data =
    record.data && typeof record.data === "object"
      ? (record.data as Record<string, unknown>)
      : null;

  const candidates = [
    record.image_url,
    record.imageUrl,
    record.output_url,
    record.outputUrl,
    record.result_url,
    record.resultUrl,
    record.url,
    data?.image_url,
    data?.imageUrl,
    data?.output_url,
    data?.outputUrl,
  ];

  const match = candidates.find(
    (value): value is string => typeof value === "string" && value.trim().length > 0
  );

  return match?.trim();
}

function resolveTemplateImageUrl(templateImageUrl?: string | null): string {
  if (templateImageUrl?.trim()) return templateImageUrl.trim();

  const category = readSelectedFifaCategory();
  if (!category) return "";

  const referenceImageUrl = category.reference_image_url?.trim();
  if (referenceImageUrl) return referenceImageUrl;

  const sliderUrls = fifaCategorySliderImageUrls(category);
  return sliderUrls[0] || fifaCategoryIconUrl(category);
}

function resolveJerseyImageUrl(jerseyImageUrl?: string | null): string {
  if (jerseyImageUrl?.trim()) return jerseyImageUrl.trim();
  return readSelectedFifaTeam()?.jersey_image?.trim() || "";
}

function capitalizeName(value: string): string {
  return value
    .trim()
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function isFifaCategoryStatsEnabled(
  category: ReturnType<typeof readSelectedFifaCategory>
): boolean {
  if (!category) return true;
  const value = category.is_stats as boolean | string | number | null | undefined;
  if (value === false || value === "false" || value === 0) return false;
  return true;
}

export async function generateFifaWorldCupCard(
  options: GenerateFifaWorldCupCardOptions = {}
): Promise<GenerateFifaWorldCupCardResult> {
  const profile = options.profile || readFifaPlayerProfile();
  const selectedCategory = readSelectedFifaCategory();
  const categoryStatsEnabled = isFifaCategoryStatsEnabled(selectedCategory);

  let isAiStats: boolean;
  let stats: FifaPlayerStats | null = null;

  if (!categoryStatsEnabled) {
    // Case 1: category is_stats false → is_ai_stats false, no stat fields
    isAiStats = false;
  } else {
    // Case 2: category is_stats true → manual stats or AI stats
    isAiStats = options.isAiStats ?? true;
    if (!isAiStats) {
      stats = options.stats || readFifaPlayerStats() || null;
    }
  }

  const sourceImageUrl = (options.sourceImageUrl || readFifaSelfie() || "").trim();
  const templateImageUrl = resolveTemplateImageUrl(options.templateImageUrl);
  const jerseyImageUrl = resolveJerseyImageUrl(options.jerseyImageUrl);
  const fifaPrompt = (options.fifaPrompt ?? selectedCategory?.prompt ?? "").trim();
  const resolvedTemplateUrl = resolveFetchableImageUrl(templateImageUrl);
  const temperature = options.temperature ?? 1.0;

  if (!profile) {
    return { success: false, error: "Missing FIFA player profile." };
  }

  if (!sourceImageUrl) {
    return { success: false, error: "Missing player selfie image." };
  }

  if (!templateImageUrl) {
    return { success: false, error: "Missing FIFA card template image." };
  }

  if (!jerseyImageUrl) {
    return { success: false, error: "Missing team jersey image." };
  }

  try {
    const fields: FifaWorldCupCardFormFields = {
      source_url: resolveFetchableImageUrl(sourceImageUrl),
      template_url: resolvedTemplateUrl,
      jersey_url: resolveFetchableImageUrl(jerseyImageUrl),
      club_team: profile.clubTeam.trim(),
      first_name: capitalizeName(profile.firstName),
      last_name: capitalizeName(profile.lastName),
      raw_prompt_mode: true,
      jersey_number: profile.jerseyNumber.trim(),
      is_ai_stats: isAiStats,
      temperature,
      fifa_prompt: fifaPrompt,
    };

    if (!isAiStats && stats) {
      fields.position = resolveFifaPositionValue(stats.position);
      fields.rating = stats.rating.trim();
      fields.pace = stats.pac;
      fields.shooting = stats.sho;
      fields.passing = stats.pas;
      fields.dribbling = stats.dri;
      fields.defending = stats.def;
      fields.physical = stats.phy;
    }

    const form = buildFifaWorldCupCardFormData(fields);

    console.log("[FIFA] generate-fifa-worldcup payload:", {
      endpoint: FIFA_GENERATION_ENDPOINT,
      ...fields,
    });

    const response = await fetchWithTimeout(FIFA_GENERATION_ENDPOINT, {
      method: "POST",
      headers: {
        "X-API-Key": process.env.REACT_APP_FAST_API_SECRET_KEY || "",
      },
      body: form,
      timeoutMs: 120000,
    });

    const rawText = await response.text();
    let json: unknown = null;

    try {
      json = rawText ? JSON.parse(rawText) : {};
    } catch {
      json = { raw: rawText };
    }

    if (!response.ok) {
      const record =
        typeof json === "object" && json ? (json as Record<string, unknown>) : {};
      const message =
        (typeof record.error === "string" && record.error) ||
        (typeof record.message === "string" && record.message) ||
        rawText ||
        `FIFA generation failed: ${response.status}`;

      return {
        success: false,
        error: message,
        data: json,
      };
    }

    const imageUrl = pickGeneratedImageUrl(json);
    if (!imageUrl) {
      return {
        success: false,
        error: "FIFA card generated, but no image URL was returned.",
        data: json,
      };
    }

    return {
      success: true,
      imageUrl,
      data: json,
    };
  } catch (error: unknown) {
    const message =
      error instanceof Error ? error.message : "Network error while generating FIFA card.";
    return {
      success: false,
      error: message,
    };
  }
}
